import { Sortable } from "@/components/Sortable/Sortable";
import {
  SortableItem,
  SortableItemHandle,
} from "@/components/Sortable/SortableItem";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Row } from "@/components/ui/flex";
import { Separator } from "@/components/ui/separator";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { kitLabel } from "@/lib/kit-label";
import { cn } from "@/lib/utils";
import { Kit } from "@/types/types";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faEllipsisVertical,
  faLock,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link, type LinkProps } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Breadcrumbs } from "./Breadcrumbs";
import { KitQuickSettingsMenu } from "./kit/KitQuickSettingsMenu";

export type KitMenuTemplateMode = "sidebar" | "topbar";

type ItemProps = {
  id: string;
  title: string;
  prefix?: string;
  icon?: IconDefinition;
  link: LinkProps;
  disabled?: boolean;
  dropdownMenu?: React.ReactElement[];
  active?: boolean;
};

type GroupMenuItemProps = {
  prefix?: string;
  id: string;
  title: string;
  icon: IconDefinition;
  onClick: () => void;
};

type GroupMenuSeparator = {
  id: string;
  separator: boolean;
  title?: string;
};

type GroupMenuProps = {
  title: string;
  icon: IconDefinition;
} & (
  | {
      link?: never;
      items: (GroupMenuItemProps | GroupMenuSeparator)[];
    }
  | {
      link: LinkProps;
      items?: never;
    }
);

type GroupProps = {
  title: string;
  showCounter?: boolean;
  active?: boolean;
  icon?: IconDefinition;
  menu?: GroupMenuProps[];
  onOrderChange?: (uuids: string[]) => void;
} & (
  | {
      link: LinkProps;
      items?: never;
      onClick?: never;
    }
  | {
      items: ItemProps[];
      link?: never;
      onClick?: never;
    }
  | {
      onClick: () => void;
      link?: never;
      items?: never;
    }
);

export type KitMenuTemplateProps = {
  mode: KitMenuTemplateMode;
  kit: Kit;
  groups: GroupProps[];
};

/**
 * Creates an adaptive menu that switches between sidebar and navigation bar modes.
 */
export function KitMenuTemplate({ kit, groups, mode }: KitMenuTemplateProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const header = (
    <SidebarGroup className="flex flex-col gap-2">
      <SidebarGroupLabel>{kitLabel(kit)}</SidebarGroupLabel>
      <SidebarMenuItem className="overflow-hidden px-2 text-sm">
        <Row align="center" justify="space-between" className="gap-2">
          <Row className="min-w-0 flex-1 overflow-hidden">
            <KitDisplayName kit={kit} />
          </Row>
          <KitQuickSettingsMenu kit={kit} />
        </Row>
      </SidebarMenuItem>
    </SidebarGroup>
  );
  const menu = (
    <Menu groups={groups} onAnyClick={() => setDropdownOpen(false)} />
  );

  const breadcrumbs: React.ReactNode[] = [];

  // Find active item in groups
  groups.forEach((group) => {
    if (group.items) {
      const activeItem = group.items.find((item) => item.active);
      if (activeItem) {
        breadcrumbs.push(
          <div className="flex flex-row items-center gap-2 overflow-hidden">
            {group.icon ? <FontAwesomeIcon icon={group.icon} /> : null}
            <div className="truncate">{group.title}</div>
          </div>,
        );
        breadcrumbs.push(
          <div className="flex flex-row items-center gap-2 overflow-hidden">
            {activeItem.icon ? (
              <FontAwesomeIcon icon={activeItem.icon} />
            ) : null}
            <div className="truncate">{activeItem.title}</div>
          </div>,
        );
      }
    } else if (group.active) {
      breadcrumbs.push(
        <div className="flex flex-row items-center gap-2 overflow-hidden">
          {group.icon ? <FontAwesomeIcon icon={group.icon} /> : null}
          <div className="truncate">{group.title}</div>
        </div>,
      );
    }
  });

  if (mode === "topbar") {
    return (
      <div className="flex flex-row items-center gap-4 overflow-hidden">
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <KitDisplayName kit={kit} />
              <FontAwesomeIcon icon={faChevronDown} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="max-w-[100vw] min-w-100"
          >
            {menu}
          </DropdownMenuContent>
        </DropdownMenu>
        <Breadcrumbs items={breadcrumbs} />
      </div>
    );
  }

  // Sidebar
  return (
    <div className="flex flex-col gap-2">
      {header}
      <Separator />
      {menu}
    </div>
  );
}

const Menu = ({
  groups,
  onAnyClick,
}: Pick<KitMenuTemplateProps, "groups"> & { onAnyClick: () => void }) => {
  const [expandedGroupIndex, setExpandedGroupIndex] = useState<number | null>(
    null,
  );

  /** Items of expanded groups. */
  const itemsToShow = useMemo((): Array<ItemProps & { id: string }> => {
    return expandedGroupIndex !== null
      ? (groups[expandedGroupIndex]?.items ?? []).map((item) => ({
          ...item,
          id: item.id,
        }))
      : [];
  }, [groups, expandedGroupIndex]);

  useEffect(() => {
    // Expand the one with active item
    const activeItemIndex = groups.findIndex(
      (group) => group.active || group.items?.some((item) => item.active),
    );
    const newExpandedItemIndex = activeItemIndex >= 0 ? activeItemIndex : 0;
    setExpandedGroupIndex(newExpandedItemIndex);
  }, [groups]);

  return (
    <SidebarGroup>
      <SidebarMenu>
        {groups.map((group, index) => {
          return (
            <SidebarMenuItem key={group.title}>
              <Collapsible
                asChild
                open={index === expandedGroupIndex}
                onOpenChange={(open) => {
                  setExpandedGroupIndex(open ? index : null);
                }}
                className="group/collapsible"
              >
                <>
                  {!group.items && (
                    <SidebarMenuButton
                      tooltip={group.title}
                      isActive={group.active}
                      className="data-[active=true]:font-normal"
                      asChild
                    >
                      <div className="group/button flex shrink-0 flex-row items-center justify-between gap-2">
                        {group.icon && <FontAwesomeIcon icon={group.icon} />}
                        {group.onClick ? (
                          <button
                            className="flex h-8 w-full flex-row items-center py-1"
                            onClick={() => {
                              group.onClick();
                              onAnyClick();
                            }}
                          >
                            {group.title}
                          </button>
                        ) : (
                          <Link
                            {...group.link}
                            className="flex h-8 w-full flex-row items-center py-1"
                            onClick={onAnyClick}
                          >
                            {group.title}
                          </Link>
                        )}
                        {group.menu && (
                          <GroupMenu
                            menu={group.menu}
                            onAnyClick={onAnyClick}
                          />
                        )}
                      </div>
                    </SidebarMenuButton>
                  )}
                  {group.items && (
                    <CollapsibleTrigger
                      asChild
                      disabled={group.items.length === 0}
                    >
                      <SidebarMenuButton
                        tooltip={group.title}
                        className="group/button flex flex-row items-center justify-between gap-2"
                        isActive={group.active}
                        disabled={
                          group.items.length === 0 && group.menu === undefined
                        }
                      >
                        <div className="flex flex-row items-center gap-2">
                          {group.icon && (
                            <div className="relative flex items-center">
                              <FontAwesomeIcon
                                icon={group.icon}
                                className="transition-opacity duration-200 group-hover/button:opacity-0 group-data-[state=open]/collapsible:opacity-0"
                              />
                              <FontAwesomeIcon
                                icon={faChevronRight}
                                className="absolute inset-0 opacity-0 transition-all duration-200 group-hover/button:opacity-100 group-data-[state=open]/button:rotate-90 group-data-[state=open]/collapsible:opacity-100"
                              />
                            </div>
                          )}
                          <span className="font-bold">{group.title}</span>
                          {group.showCounter && (
                            <Badge className="text-xs" variant="outline">
                              {group.items.length}
                            </Badge>
                          )}
                        </div>
                        {group.menu && (
                          <GroupMenu
                            menu={group.menu}
                            onAnyClick={onAnyClick}
                          />
                        )}
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                  )}
                  {group.items &&
                    group.items.length > 0 &&
                    index === expandedGroupIndex && (
                      <CollapsibleContent>
                        <SidebarMenuSub className="relative overflow-hidden">
                          <Sortable
                            disabled={!group.onOrderChange}
                            items={itemsToShow}
                            onChange={(items) => {
                              group.onOrderChange?.(
                                items.map((item) => String(item.id)),
                              );
                            }}
                            itemStringifier={({ dropdownMenu: _, ...rest }) =>
                              JSON.stringify(rest)
                            }
                            renderItem={({ item, isDraggingAnother }) => (
                              <SortableItem
                                id={item.id}
                                as={SidebarMenuSubItem}
                                key={item.id}
                              >
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={item.active}
                                >
                                  <div className="group/sidebar-menu-sub-item flex flex-row items-center justify-between gap-2">
                                    <Row className="flex flex-1 flex-row items-center justify-between gap-2 overflow-hidden">
                                      {item.disabled ? (
                                        <span
                                          className="text-muted-foreground flex h-8 flex-1 flex-row items-center truncate py-1.5"
                                          title="Visible in this kit, but you do not have access to open it."
                                        >
                                          {item.prefix && (
                                            <>
                                              <span className="font-family-fancy text-foreground-5 inline-block max-w-1/4 truncate text-xs">
                                                {item.prefix.slice(0, -1)}
                                              </span>
                                              <span className="font-family-fancy text-foreground-5 text-xs">
                                                /
                                              </span>
                                            </>
                                          )}
                                          <span
                                            className={cn("truncate", {
                                              "max-w-3/4": item.prefix,
                                            })}
                                          >
                                            {item.title}
                                          </span>
                                          <FontAwesomeIcon
                                            icon={faLock}
                                            className="ml-1 shrink-0"
                                          />
                                        </span>
                                      ) : (
                                        <Link
                                          {...item.link}
                                          className="flex h-8 flex-1 flex-row items-center truncate py-1.5"
                                          onClick={onAnyClick}
                                        >
                                          {item.prefix && (
                                            <>
                                              <span className="font-family-fancy text-foreground-5 inline-block max-w-1/4 truncate text-xs">
                                                {item.prefix.slice(0, -1)}
                                              </span>
                                              <span className="font-family-fancy text-foreground-5 text-xs">
                                                /
                                              </span>
                                            </>
                                          )}
                                          <span
                                            className={cn("truncate", {
                                              "max-w-3/4": item.prefix,
                                            })}
                                          >
                                            {item.title}
                                          </span>
                                        </Link>
                                      )}
                                      {group.onOrderChange &&
                                        !isDraggingAnother && (
                                          <SortableItemHandle className="hidden group-hover/sidebar-menu-sub-item:block" />
                                        )}
                                    </Row>
                                    {item.dropdownMenu && (
                                      <ItemDropdownMenu
                                        items={item.dropdownMenu}
                                        active={!!item.active}
                                        onAnyClick={onAnyClick}
                                      />
                                    )}
                                  </div>
                                </SidebarMenuSubButton>
                              </SortableItem>
                            )}
                          ></Sortable>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    )}
                </>
              </Collapsible>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
};

const ItemDropdownMenu = ({
  items,
  active,
  onAnyClick,
}: {
  items: React.ReactElement[];
  active: boolean;
  onAnyClick: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) setVisible(true);
    if (!isOpen) setTimeout(() => setVisible(false), 100);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger
        asChild
        className={cn(
          "hidden flex-row items-center gap-2 group-hover/sidebar-menu-sub-item:flex",
          {
            flex: active || visible,
          },
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="icon-sm"
          title="More Actions"
          aria-label="More Actions"
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="flex flex-col">
        {items.map((item, index) => (
          <DropdownMenuItem
            key={index}
            asChild
            className="w-full"
            onClick={onAnyClick}
          >
            {item}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const GroupMenu = ({
  menu,
  onAnyClick,
}: {
  menu: GroupMenuProps[];
  onAnyClick: () => void;
}) => {
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const handleItemClick = () => {
    setDropdownOpen(null);
  };

  return (
    <div
      className="flex flex-row items-center"
      onClick={(e) => e.stopPropagation()}
    >
      {menu.map((m) =>
        m.link ? (
          <Link
            key={m.title}
            {...m.link}
            onClick={(e) => {
              onAnyClick();
              e.stopPropagation();
            }}
          >
            <Button variant="ghost" size="icon-sm" title={m.title}>
              <FontAwesomeIcon icon={m.icon} />
            </Button>
          </Link>
        ) : (
          <DropdownMenu
            key={m.title}
            modal={false}
            open={dropdownOpen === m.title}
            onOpenChange={(isOpen) => {
              setDropdownOpen(isOpen ? m.title : null);
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={m.title}
                title={m.title}
              >
                <FontAwesomeIcon icon={m.icon} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-w-[80dvw]">
              {m.items.map((item) => {
                if ("separator" in item) {
                  if (!item.separator) return null;
                  return (
                    <>
                      <DropdownMenuSeparator key={item.id} />
                      {item.title && (
                        <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
                      )}
                    </>
                  );
                }

                return (
                  <DropdownMenuItem
                    key={item.id}
                    onClick={() => {
                      item.onClick();
                      handleItemClick();
                      onAnyClick();
                    }}
                    className="gap-0 truncate"
                  >
                    <FontAwesomeIcon icon={item.icon} className="mr-2" />
                    {item.prefix && (
                      <span className="font-family-fancy text-foreground-5 max-w-1/2 truncate text-xs">
                        {item.prefix}
                      </span>
                    )}
                    <span className="truncate">{item.title}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      )}
    </div>
  );
};
